/**
 * SmartPitch API client.
 * All requests go through this module for consistent auth and error handling.
 */

import axios, { AxiosInstance } from 'axios';
import type {
  SiteCreate,
  SiteUpdate,
  EngineerSiteAccessUpdate,
  HeatCurve,
  PidParams,
  ScheduleEntry,
  CameraRegister,
  OrgUpdate,
  OrgSubscriptionUpdate,
  InvoiceCreate,
  InvoiceUpdate,
  InvoiceSettings,
  Camera,
  SettingsPayload,
  AlertPreferences,
  CircuitCreate,
  CircuitUpdate,
} from './types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sp_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — try refreshing the token before force-logging out
let isRefreshing = false;
let isLoggingOut = false;
let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Server-side must-change boxing (2026-07): while the flag is set the
    // API 403s everything outside the change-password surface. Without
    // this redirect a boxed session that reloads or deep-links lands on a
    // silently broken page (pollers 403 forever, no way out) — send them
    // to the change-password screen instead.
    if (
      error.response?.status === 403 &&
      typeof error.response?.data?.detail === 'string' &&
      error.response.data.detail.startsWith('Password change required') &&
      window.location.pathname !== '/change-password'
    ) {
      window.location.href = '/change-password';
      return new Promise(() => {});
    }
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/change-password') ||
        url.includes('/auth/reset-password') ||
        url.includes('/auth/refresh');
      if (!isAuthEndpoint) {
        // Already redirecting to login — silently reject to prevent error toasts
        if (isLoggingOut) {
          return new Promise(() => {});
        }
        // Try refreshing the token before giving up
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = api
            .post('/auth/refresh')
            .then((res) => {
              const newToken: string = res.data.token;
              localStorage.setItem('sp_token', newToken);
              return newToken;
            })
            .catch(() => null)
            .finally(() => {
              isRefreshing = false;
            });
        }
        const newToken = await refreshPromise;
        if (newToken) {
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        }
        // Refresh failed — force logout (first 401 to reach here wins)
        if (!isLoggingOut) {
          isLoggingOut = true;
          localStorage.removeItem('sp_token');
          localStorage.removeItem('sp_user');
          window.location.href = '/login';
        }
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

// ---- Auth ----
export const authApi = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/auth/change-password', { current_password, new_password }),
  invite: (email: string, name: string, role: string, org_id?: string, language?: string) =>
    api.post('/auth/invite', { email, name, role, ...(org_id ? { org_id } : {}), ...(language ? { language } : {}) }),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) => api.post('/auth/reset-password', { token, new_password }),
  updateLanguage: (language: string) => api.put('/auth/me/language', { language }),
};

// ---- Fleet ----
export const fleetApi = {
  overview: () => api.get('/fleet/'),
  dashboard: () => api.get('/fleet/dashboard'),
};

// ---- Engineers ----
export const engineersApi = {
  list: () => api.get('/engineers/'),
  get: (id: string) => api.get(`/engineers/${id}`),
  updateSiteAccess: (engineerId: string, assignments: EngineerSiteAccessUpdate[]) =>
    api.put(`/engineers/${engineerId}/sites`, assignments),
  getMyPermissions: (siteId: string) => api.get(`/engineers/me/permissions/${siteId}`),
};

// ---- Sites ----
export const sitesApi = {
  list: () => api.get('/sites/'),
  get: (id: string) => api.get(`/sites/${id}`),
  create: (data: SiteCreate) => api.post('/sites/', data),
  update: (id: string, data: SiteUpdate) => api.patch(`/sites/${id}`, data),
  delete: (id: string) => api.delete(`/sites/${id}`),
  getFeatureFlags: (id: string) => api.get(`/sites/${id}/feature-flags`),
};

// ---- Site-scoped ----
export const siteApi = (siteId: string) => ({
  // GUI — main aggregated state (polled every 10s)
  gui: () => api.get(`/sites/${siteId}/gui`),

  // Status
  status: () => api.get(`/sites/${siteId}/status`),
  trends: (hours = 24) => api.get(`/sites/${siteId}/trends`, { params: { hours } }),

  // Timeseries
  // `until` anchors the window's end (ISO string); omitted = now.
  timeseries: (hours = 24, until?: string) =>
    api.get(`/sites/${siteId}/timeseries`, { params: { hours, ...(until ? { until } : {}) } }),

  // Server-rendered exports for the Data tab. Full-resolution CSV of
  // every trend_log row in [from, to), and the branded PDF report.
  exportTrendCsv: (from: string, to: string) =>
    api.get(`/sites/${siteId}/export/trend.csv`, { params: { from, to }, responseType: 'blob' }),
  exportReportPdf: (from: string, to: string) =>
    api.get(`/sites/${siteId}/export/report.pdf`, { params: { from, to }, responseType: 'blob' }),

  // Mode & settings
  getMode: () => api.get(`/sites/${siteId}/mode`),
  setMode: (mode: string) => api.post(`/sites/${siteId}/mode`, { mode }),
  setSystemEnable: (enabled: boolean) => api.post(`/sites/${siteId}/system-enable`, null, { params: { enabled } }),
  getSetting: (key: string) => api.get(`/sites/${siteId}/settings/${key}`),
  setSetting: (key: string, value: unknown) => api.post(`/sites/${siteId}/settings/${key}`, value),
  setGenericSetting: (key: string, value: unknown) => api.post(`/sites/${siteId}/settings`, { key, value }),

  // Heat curve
  getHeatCurve: () => api.get(`/sites/${siteId}/heatcurve`),
  setHeatCurve: (data: HeatCurve) => api.post(`/sites/${siteId}/heatcurve`, data),

  // PID
  getPid: () => api.get(`/sites/${siteId}/pid`),
  setPid: (data: PidParams) => api.post(`/sites/${siteId}/pid`, data),

  // Schedule
  //
  // Migration 076: GET returns `{sessions, version, window_start}` instead
  // of a bare list. POST accepts `{sessions, expected_version, delete_window_start}`
  // for optimistic-lock + bounded-replace. See routes/settings_routes.py
  // and SiteSchedule.tsx for the full contract. The monitor-side readers
  // (ForecastTab, ScheduleTab) accept both shapes for safety while old
  // and new versions might coexist during the rollout.
  getSchedule: (params?: { include_past?: boolean }) => api.get(`/sites/${siteId}/schedule`, { params }),
  setSchedule: (
    sessions: ScheduleEntry[],
    opts?: { expected_version?: string | null; delete_window_start?: string | null }
  ) =>
    api.post(`/sites/${siteId}/schedule`, {
      sessions,
      expected_version: opts?.expected_version ?? null,
      delete_window_start: opts?.delete_window_start ?? null,
    }),

  // Pitches
  getPitches: () => api.get(`/sites/${siteId}/pitches`),

  // Lead time & suppression window
  getLeadTime: () => api.get(`/sites/${siteId}/lead-time`),
  // The schedule warning's two customer controls (routes/settings_routes.py 'schedule_warning')
  getScheduleWarningSettings: () => api.get(`/sites/${siteId}/settings/schedule_warning`),
  setScheduleWarningSettings: (data: { email_enabled?: boolean; sensitivity?: 'cautious' | 'normal' | 'relaxed' }) =>
    api.post(`/sites/${siteId}/settings/schedule_warning`, data),
  setLeadTime: (data: { default_heating_lead_time_minutes?: number; floodlight_suppression_window_minutes?: number }) =>
    api.post(`/sites/${siteId}/lead-time`, data),

  // Sessions
  getSessions: (limit = 20, circuitId?: string | null) =>
    api.get(`/sites/${siteId}/sessions`, {
      params: { limit, ...(circuitId ? { circuit_id: circuitId } : {}) },
    }),
  getSession: (sessionId: number) => api.get(`/sites/${siteId}/sessions/${sessionId}`),

  // Energy
  getEnergyCost: (days = 30) => api.get(`/sites/${siteId}/energy/cost`, { params: { days } }),
  setEnergyRate: (rate: number) => api.post(`/sites/${siteId}/energy/rate`, null, { params: { rate_per_kwh: rate } }),
  getEnergyChart: (start: string, end: string, group = 'daily', circuitId?: string | null) =>
    api.get(`/sites/${siteId}/energy/chart`, {
      params: { start, end, group, ...(circuitId ? { circuit_id: circuitId } : {}) },
    }),
  getEnergyForecast: () => api.get(`/sites/${siteId}/energy/forecast`),

  // Advisory snapshot — composed payload driving the narrative engine
  // on the Heating > Advisory tab (Phase 1 of the option-C rewrite).
  getAdvisorySnapshot: () => api.get(`/sites/${siteId}/advisory/snapshot`),
  // Energy Insights (Phase 12 — defensible facts only, replaces
  // the savings claim). One-shot load on advisory tab open.
  getAdvisoryEnergyInsights: (n: number = 5) =>
    api.get(`/sites/${siteId}/advisory/energy-insights`, { params: { n_seasons: n } }),

  // Forecast
  getForecast: (hours = 168) => api.get(`/sites/${siteId}/forecast`, { params: { hours } }),

  // Frost
  getFrostStatus: () => api.get(`/sites/${siteId}/frost/status`),
  getFrostSettings: () => api.get(`/sites/${siteId}/settings/frost_config`),
  setFrostSettings: (data: SettingsPayload) => api.post(`/sites/${siteId}/settings/frost_config`, data),

  // Cold gap — the site's learned "colder than its forecast" number
  // (migration 087). Reset forgets the learning; manual replaces it
  // with a measured value (null clears the manual value).
  getColdGap: () => api.get(`/sites/${siteId}/cold-gap`),
  // Snow protection (migration 099): the plan and the record; the two
  // settings save through routes/settings_routes.py 'snow'
  getSnow: () => api.get(`/sites/${siteId}/snow`),
  setSnowSettings: (data: { protect?: string; target_c?: number }) => api.post(`/sites/${siteId}/settings/snow`, data),
  resetColdGap: () => api.post(`/sites/${siteId}/cold-gap/reset`),
  // the schedule warning (migration 092): the week ahead judged, the site's record
  getScheduleWarnings: () => api.get(`/sites/${siteId}/schedule-warnings`),
  evaluateScheduleWarnings: () => api.post(`/sites/${siteId}/schedule-warnings/evaluate`),
  setColdGapManual: (manualC: number | null) => api.post(`/sites/${siteId}/cold-gap/manual`, { manual_c: manualC }),

  // Decisions history
  getDecisions: (hours = 24, circuitId?: string | null, until?: string, limit?: number) =>
    api.get(`/sites/${siteId}/decisions`, {
      params: {
        hours,
        ...(circuitId ? { circuit_id: circuitId } : {}),
        ...(until ? { until } : {}),
        ...(limit ? { limit } : {}),
      },
    }),

  // Learning
  getLearningBands: (circuitId?: string | null) =>
    api.get(`/sites/${siteId}/learning/bands`, {
      params: circuitId ? { circuit_id: circuitId } : {},
    }),

  // Hardware Alarm Settings (from site_settings.alarm_settings)
  getAlarmSettings: () => api.get(`/sites/${siteId}/settings/alarm_settings`),
  setAlarmSettings: (data: SettingsPayload) => api.post(`/sites/${siteId}/settings/alarm_settings`, data),

  // Alert Notification System
  getAlerts: (params?: { status?: string; severity?: string; limit?: number }) =>
    api.get(`/sites/${siteId}/alerts`, { params }),
  getActiveAlerts: () => api.get(`/sites/${siteId}/alerts/active`),
  acknowledgeAlerts: (alert_ids: string[]) => api.post(`/sites/${siteId}/alerts/acknowledge`, { alert_ids }),
  resolveAlert: (alertId: string) => api.post(`/sites/${siteId}/alerts/resolve/${alertId}`),
  getAlertPreferences: () => api.get(`/sites/${siteId}/alerts/preferences`),
  setAlertPreferences: (data: Partial<AlertPreferences>) => api.put(`/sites/${siteId}/alerts/preferences`, data),
  testAlertEmail: (recipient?: string) =>
    api.post(`/sites/${siteId}/alarms/test-email`, recipient ? { recipient } : {}),
  getAlertHistory: (days = 7) => api.get(`/sites/${siteId}/alerts/history`, { params: { days } }),

  // Modbus / Soil Scout
  getModbusStatus: () => api.get(`/sites/${siteId}/modbus/status`),

  // Soil Scout (per-site)
  soilScout: {
    // Wizard step 1: probe customer creds, list their Soil Scout sites
    discover: (username: string, password: string) =>
      api.post(`/sites/${siteId}/soil-scout/connect`, { username, password }),
    // Wizard step 2: provision integration user + persist link
    link: (req: { username: string; password: string; soilscout_site_id: number; test_mode?: boolean }) =>
      api.post(`/sites/${siteId}/soil-scout/link`, req),
    getLink: () => api.get(`/sites/${siteId}/soil-scout/link`),
    updateLink: (data: { test_mode?: boolean }) => api.patch(`/sites/${siteId}/soil-scout/link`, data),
    unlink: () => api.delete(`/sites/${siteId}/soil-scout/link`),
    listSensors: () => api.get(`/sites/${siteId}/soil-sensors`),
    discoverSensors: () => api.post(`/sites/${siteId}/soil-sensors/discover`),
    updateSensorMapping: (
      sensorId: string,
      data: {
        role: string;
        pitch_index?: number | null;
        zone_index?: number | null;
        pos_x?: number | null;
        pos_y?: number | null;
      }
    ) => api.patch(`/sites/${siteId}/soil-sensors/${sensorId}`, data),
    diagnostics: () => api.get(`/sites/${siteId}/soil-diagnostics`),
    // every site user: each sensor's place and latest reading, for the pitch drawing
    sensorReadings: () => api.get(`/sites/${siteId}/soil-sensor-readings`),
  },

  // Forecast settings
  getForecastSettings: () => api.get(`/sites/${siteId}/settings/forecast`),
  setForecastSettings: (data: SettingsPayload) => api.post(`/sites/${siteId}/settings/forecast`, data),

  // SmartPitch settings
  getSmartPitchSettings: () => api.get(`/sites/${siteId}/settings/smartpitch_settings`),
  setSmartPitchSettings: (data: SettingsPayload) => api.post(`/sites/${siteId}/settings/smartpitch_settings`, data),

  // Standard mode settings
  getStandardSettings: () => api.get(`/sites/${siteId}/settings/standard`),
  setStandardSettings: (data: SettingsPayload) => api.post(`/sites/${siteId}/settings/standard`, data),

  // Devices
  getDevices: () => api.get(`/sites/${siteId}/devices/`),
  registerDevice: (serial: string, name?: string) =>
    api.post(`/sites/${siteId}/devices/`, { serial_number: serial, name }),
  deleteDevice: (deviceId: string) => api.delete(`/sites/${siteId}/devices/${deviceId}`),

  // Install tokens (per-install secure provisioning — see routes/install.py).
  // scope: 'edge_gateway' (RevPi), 'camera_router' (Teltonika RUT951 for a
  // remote Dahua camera), 'cellular_router' (RUT in front of a wired-less
  // RevPi). sim_type only matters for the RUT scopes — Phase 1 rejects
  // 'esim' at the API layer, so the type allows it but the call will 400.
  // lan_cidr (Phase 1.5) is the LAN CIDR the RUT serves on its LAN side
  // so cloud can reach the camera/RevPi behind it via the tunnel.
  // Ignored for edge_gateway tokens.
  // camera_index (migration 066) lets the orchestrator map a realised
  // WG peer 1:1 to its pending cameras row at multi-Dahua sites. Only
  // accepted for scope='camera_router'.
  issueInstallToken: (
    scope: 'edge_gateway' | 'camera_router' | 'cellular_router',
    ttl_minutes = 60,
    sim_type: 'physical' | 'esim' = 'physical',
    lan_cidr?: string,
    camera_index?: number
  ) =>
    api.post(`/sites/${siteId}/install-tokens`, {
      scope,
      ttl_minutes,
      sim_type,
      ...(lan_cidr ? { lan_cidr } : {}),
      ...(camera_index !== undefined ? { camera_index } : {}),
    }),
  listInstallTokens: () => api.get(`/sites/${siteId}/install-tokens`),
  getInstallToken: (tokenId: string) => api.get(`/sites/${siteId}/install-tokens/${tokenId}`),
  revokeInstallToken: (tokenId: string) => api.post(`/sites/${siteId}/install-tokens/${tokenId}/revoke`),

  // Device I/O Config
  getIOConfig: (deviceId: string) => api.get(`/sites/${siteId}/devices/${deviceId}/io-config`),
  updateIOConfig: (
    deviceId: string,
    data: { io_map: Record<string, unknown>; safety_config?: Record<string, unknown> }
  ) => api.post(`/sites/${siteId}/devices/${deviceId}/io-config`, data),
  resetIOConfig: (deviceId: string) => api.post(`/sites/${siteId}/devices/${deviceId}/io-config/reset`),

  // Device I/O Features (function-based config)
  getIOFeatures: (deviceId: string) => api.get(`/sites/${siteId}/devices/${deviceId}/io-features`),
  updateIOFeatures: (deviceId: string, io_features: Record<string, unknown>) =>
    api.put(`/sites/${siteId}/devices/${deviceId}/io-features`, { io_features }),
  validateIOFeatures: (deviceId: string, io_features: Record<string, unknown>) =>
    api.post(`/sites/${siteId}/devices/${deviceId}/io-features/validate`, { io_features }),
  inferIOFeatures: (deviceId: string) => api.post(`/sites/${siteId}/devices/${deviceId}/io-features/infer`),

  // Device Hardware / Auto-Provisioning
  getDeviceHardware: (deviceId: string) => api.get(`/sites/${siteId}/devices/${deviceId}/hardware`),
  pushPiControlConfig: (deviceId: string) => api.post(`/sites/${siteId}/devices/${deviceId}/push-picontrol-config`),

  // Live I/O
  getIOLive: () => api.get(`/sites/${siteId}/io/live`),

  // Faults
  getFaults: () => api.get(`/sites/${siteId}/plc/faults`),

  // Manual Override
  getManualOverride: () => api.get(`/sites/${siteId}/manual-override`),
  setManualOverride: (state: Record<string, unknown>) => api.post(`/sites/${siteId}/manual-override`, state),
  toggleManualOverride: (enabled: boolean) => api.post(`/sites/${siteId}/manual-override/toggle`, { enabled }),
  setManualDO: async (pin: number, state: boolean) => {
    // GET current state, merge the single DO change into nested do object, POST full state
    const { data: current } = await api.get(`/sites/${siteId}/manual-override`);
    const doState = { ...(current.do || {}), [`do${pin}`]: state };
    return api.post(`/sites/${siteId}/manual-override`, { ...current, do: doState });
  },
  setManualAO: async (pin: number, value: number, _enabled: boolean) => {
    // GET current state, merge the single AO change into nested ao object, POST full state
    const { data: current } = await api.get(`/sites/${siteId}/manual-override`);
    const aoState = { ...(current.ao || {}), [`ao${pin}`]: value };
    return api.post(`/sites/${siteId}/manual-override`, { ...current, ao: aoState });
  },

  // Commissioning
  getCommissioningReports: () => api.get(`/sites/${siteId}/commissioning/reports`),
  saveCommissioningReport: (data: { status?: string; report_data?: Record<string, unknown> }) =>
    api.post(`/sites/${siteId}/commissioning/reports`, data),
  deleteCommissioningReport: (reportId: string) => api.delete(`/sites/${siteId}/commissioning/reports/${reportId}`),

  // Cameras (multi-camera)
  getCameras: () => api.get(`/sites/${siteId}/cameras`),
  getCamera: (cameraId: number) => api.get(`/sites/${siteId}/cameras/${cameraId}`),
  registerCamera: (data: CameraRegister) => api.post(`/sites/${siteId}/cameras`, data),
  updateCamera: (cameraId: number, data: Partial<Camera>) => api.patch(`/sites/${siteId}/cameras/${cameraId}`, data),
  deleteCamera: (cameraId: number) => api.delete(`/sites/${siteId}/cameras/${cameraId}`),
  testCamera: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/test`),
  // Re-queue a failed Dahua auto-provisioning (migration 065 + orchestrator).
  // Resets cameras.provisioning_status='pending' and clears the matching
  // wireguard_peers.camera_provisioned_at so the orchestrator picks it up
  // again within ~30s. Only valid when current status='failed'.
  retryProvisionCamera: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/retry-provision`),
  updateCameraROI: (cameraId: number, roi: Array<Record<string, unknown>>) =>
    api.put(`/sites/${siteId}/cameras/${cameraId}/roi`, { roi }),
  getCamerasStatus: () => api.get(`/sites/${siteId}/cameras/status`),
  getCamerasAudit: (hours = 24, eventType?: string) =>
    api.get(`/sites/${siteId}/cameras/audit`, { params: { hours, ...(eventType ? { event_type: eventType } : {}) } }),
  startCameraPreview: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/preview/start`),
  getCameraPreviewSnapshot: (cameraId: number, token: string) =>
    api.get(`/sites/${siteId}/cameras/${cameraId}/preview/snapshot`, { params: { token }, responseType: 'blob' }),
  startCommissioning: (cameraId: number, durationHours: number) =>
    api.post(`/sites/${siteId}/cameras/${cameraId}/commissioning/start`, { duration_hours: durationHours }),
  stopCommissioning: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/commissioning/stop`),
  getCommissioningStatus: (cameraId: number) => api.get(`/sites/${siteId}/cameras/${cameraId}/commissioning/status`),
  // Phase 19l-B: calibration sample collection is decoupled from the
  // commissioning_mode editing window. The duration buttons should call
  // startCalibration / stopCalibration, NOT startCommissioning.
  startCalibration: (cameraId: number, durationHours: number) =>
    api.post(`/sites/${siteId}/cameras/${cameraId}/calibration/start`, { duration_hours: durationHours }),
  stopCalibration: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/calibration/stop`),
  suggestThresholds: (cameraId: number) => api.post(`/sites/${siteId}/cameras/${cameraId}/suggest-thresholds`),
  getCalibrationStatus: (cameraId: number) => api.get(`/sites/${siteId}/cameras/${cameraId}/calibration-status`),
  pauseAllCameras: () => api.post(`/sites/${siteId}/cameras/pause-all`),
  resumeAllCameras: () => api.post(`/sites/${siteId}/cameras/resume-all`),

  // Commissioning approval
  requestCommissioning: (data: {
    camera_id?: number;
    reason: string;
    duration_hours: number;
    requested_start?: string;
  }) => api.post(`/sites/${siteId}/commissioning/request`, data),
  getCommissioningRequests: () => api.get(`/sites/${siteId}/commissioning/requests`),
  getActiveCommissioning: () => api.get(`/sites/${siteId}/commissioning/active`),

  // Master user & camera permissions
  setMasterUser: (userId: string) => api.patch(`/sites/${siteId}/master-user`, { user_id: userId }),
  getApprovalContactCandidates: () => api.get(`/sites/${siteId}/approval-contact-candidates`),
  setCameraPermissions: (data: { camera_visible_to_customers?: boolean; camera_visible_to_engineers?: boolean }) =>
    api.patch(`/sites/${siteId}/camera-permissions`, data),

  // Setup Wizard
  saveSetupWizard: (data: Record<string, unknown>) => api.post(`/sites/${siteId}/setup-wizard/save`, data),

  // Circuits
  getCircuits: () => api.get(`/sites/${siteId}/circuits/`),
  getCircuit: (circuitId: string) => api.get(`/sites/${siteId}/circuits/${circuitId}`),
  createCircuit: (data: CircuitCreate) => api.post(`/sites/${siteId}/circuits/`, data),
  updateCircuit: (circuitId: string, data: CircuitUpdate) => api.patch(`/sites/${siteId}/circuits/${circuitId}`, data),
  deleteCircuit: (circuitId: string) => api.delete(`/sites/${siteId}/circuits/${circuitId}`),
  getCircuitHeatCurve: (circuitId: string) => api.get(`/sites/${siteId}/circuits/${circuitId}/heatcurve`),
  setCircuitHeatCurve: (circuitId: string, data: HeatCurve) =>
    api.post(`/sites/${siteId}/circuits/${circuitId}/heatcurve`, data),
  resetCircuitHeatCurve: (circuitId: string) => api.delete(`/sites/${siteId}/circuits/${circuitId}/heatcurve`),
});

// ---- Organisation ----
export const orgApi = {
  get: () => api.get('/org/'),
  update: (data: OrgUpdate) => api.put('/org/', data),
  updateSubscription: (data: OrgSubscriptionUpdate) => api.put('/org/subscription', data),
  completeOnboarding: () => api.post('/org/onboarding-complete'),
  getUsage: () => api.get('/org/usage'),
  // Platform customer management
  createCustomer: (data: OrgUpdate) => api.post('/org/customers', data),
  listCustomers: (includeArchived = false) =>
    api.get('/org/customers', { params: includeArchived ? { include_archived: true } : {} }),
  getCustomer: (orgId: string) => api.get(`/org/customers/${orgId}`),
  updateCustomer: (orgId: string, data: OrgUpdate) => api.put(`/org/customers/${orgId}`, data),
  updateCustomerSubscription: (orgId: string, data: OrgSubscriptionUpdate) =>
    api.put(`/org/customers/${orgId}/subscription`, data),
  getCustomerUsage: (orgId: string) => api.get(`/org/customers/${orgId}/usage`),
  getCustomerSites: (orgId: string) => api.get(`/org/customers/${orgId}/sites`),
  getCustomerUsers: (orgId: string) => api.get(`/org/customers/${orgId}/users`),
  getCustomerFeatures: (orgId: string) => api.get(`/org/customers/${orgId}/features`),
  updateCustomerFeatures: (orgId: string, feature_flags: Record<string, boolean>) =>
    api.put(`/org/customers/${orgId}/features`, { feature_flags }),
  deleteCustomer: (orgId: string) => api.delete(`/org/customers/${orgId}`),
  permanentlyDeleteCustomer: (orgId: string) => api.delete(`/org/customers/${orgId}/permanent`),
  restoreCustomer: (orgId: string) => api.post(`/org/customers/${orgId}/restore`),
  // Soil Scout (org-level)
  soilScout: {
    getStatus: () => api.get('/org/soil-scout/status'),
    rotatePassword: () => api.post('/org/soil-scout/rotate-password'),
    disconnect: () => api.delete('/org/soil-scout'),
  },
};

// ---- I/O Templates ----
export const ioTemplatesApi = {
  list: () => api.get('/io-templates'),
};

// ---- Users ----
export const usersApi = {
  list: () => api.get('/users/'),
  changeRole: (userId: string, role: string) => api.post(`/users/${userId}/role`, null, { params: { role } }),
  deactivate: (userId: string) => api.post(`/users/${userId}/deactivate`),
};

// ---- Invoices ----
export const invoiceApi = {
  list: (params?: { org_id?: string; status?: string }) => api.get('/invoices/', { params }),
  get: (id: string) => api.get(`/invoices/${id}`),
  create: (data: InvoiceCreate) => api.post('/invoices/', data),
  update: (id: string, data: InvoiceUpdate) => api.put(`/invoices/${id}`, data),
  addItem: (id: string, item: { description: string; quantity: number; unit_price: number; item_type?: string }) =>
    api.post(`/invoices/${id}/add-item`, item),
  removeItem: (id: string, itemId: string) => api.delete(`/invoices/${id}/items/${itemId}`),
  send: (id: string) => api.post(`/invoices/${id}/send`),
  markPaid: (id: string) => api.post(`/invoices/${id}/mark-paid`),
  cancel: (id: string) => api.post(`/invoices/${id}/cancel`),
  deleteInvoice: (id: string) => api.delete(`/invoices/${id}`),
  renew: (id: string, poNumber?: string) => api.post(`/invoices/${id}/renew`, poNumber ? { po_number: poNumber } : {}),
  downloadPdf: (id: string) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  generate: (orgId: string, poNumber?: string) =>
    api.post(`/invoices/generate/${orgId}`, poNumber ? { po_number: poNumber } : {}),
  getSettings: () => api.get('/invoices/settings'),
  updateSettings: (data: Partial<InvoiceSettings>) => api.put('/invoices/settings', data),
};

// ---- Audit Log ----
export const auditApi = {
  list: (params?: Record<string, any>) => api.get('/audit-log/', { params }),
  exportCsv: (params?: Record<string, any>) => api.get('/audit-log/export', { params, responseType: 'blob' }),
};

// ---- SIM Inventory (HQ, platform-only) ----
//
// Mirrors cloud/api/routes/sim_inventory.py. The SimInventory ORM
// row shape (camelCased here) is returned by every endpoint that
// mutates a SIM, so the page can re-render from the response without
// a follow-up GET.
export interface SimInventoryRow {
  iccid: string;
  serial_number: string;
  plan_id: string | null;
  simbase_state: 'enabled' | 'disabled' | 'enabling' | 'disabling';
  simbase_name: string | null;
  simbase_tags: string[];
  eid: string | null;
  assigned_site_id: string | null;
  assigned_scope: 'camera_router' | 'cellular_router' | null;
  assigned_camera_index: number | null;
  assigned_install_token_id: string | null;
  current_month_data_bytes: number;
  current_month_cost: string;
  current_month_cost_currency: string;
  // F4 (2026-06-26) — mirrored from Simbase SimcardListItem. All
  // optional because the columns are nullable (existing rows pre-
  // migration carry null until the next reconcile back-fills them).
  coverage?: string | null;
  sim_profile?: string | null;
  imei?: string | null;
  imei_lock?: 'on' | 'off' | null;
  hardware?: string | null;
  autodisable?: string | null; // ISO date YYYY-MM-DD
  usage_limits_data_threshold?: number | null;
  usage_limits_sms_threshold?: number | null;
  usage_limits_auto_enable?: boolean | null;
  last_synced_at: string | null;
  created_at: string;
  suspended_at: string | null;
}

export interface SimInventoryListResponse {
  sims: SimInventoryRow[];
  total: number;
}

export const simInventoryApi = {
  list: (params?: { state?: string; site_id?: string; unassigned_only?: boolean }) =>
    api.get<SimInventoryListResponse>('/admin/sims', { params }),
  get: (iccid: string) => api.get<SimInventoryRow>(`/admin/sims/${iccid}`),
  add: (data: {
    iccid: string;
    // 2026-06-26 F25: optional. Backend GET-probes Simbase first;
    // for SIMs already in the account (store-bought) serial_number
    // is not needed — and Simbase doesn't expose it anyway.
    serial_number?: string;
    plan_id?: string;
    activation_code?: string;
    simbase_name?: string;
    enable_on_simbase?: boolean;
  }) => api.post<SimInventoryRow>('/admin/sims', data),
  assign: (
    iccid: string,
    data: { site_id: string; scope: 'camera_router' | 'cellular_router'; camera_index?: number }
  ) => api.post<SimInventoryRow>(`/admin/sims/${iccid}/assign`, data),
  unassign: (iccid: string) => api.post<SimInventoryRow>(`/admin/sims/${iccid}/unassign`),
  setState: (iccid: string, state: 'enabled' | 'disabled') =>
    api.post<SimInventoryRow>(`/admin/sims/${iccid}/state`, { state }),
  delete: (iccid: string) => api.delete<SimInventoryRow>(`/admin/sims/${iccid}`),
  sync: () => api.post<{ status: string; report: Record<string, any> }>('/admin/sims/sync'),
};

// Wizard-side: triggered when the engineer hits the Issue button on
// an eSIM slot with no SIM allocated. Backend rate-limits per slot to
// 1 email per hour so spam-clicking won't flood HQ's inbox.
export const requestSimApi = {
  request: (
    siteId: string,
    data: { site_id: string; scope: 'camera_router' | 'cellular_router'; camera_index?: number }
  ) => api.post<{ status: string; rate_limited: boolean }>(`/sites/${siteId}/request-sim`, data),
  listAssigned: (siteId: string) => api.get<SimInventoryListResponse>(`/sites/${siteId}/sims`),
};

export default api;
